import React from "react";
import { TimeSeries } from "../../common/src";
import { numberFormatFun } from "../../common/src/utils/FormatFunctions";
import { cloneDeep } from "lodash";

const format = numberFormatFun("0.0%");

type PeriodReturnsTableInput = {
    timeseries: TimeSeries[];
    ranges: string[];
};

export const PeriodReturnsTable = ({ timeseries, ranges }: PeriodReturnsTableInput): React.ReactElement => {
    const allReturns: number[][] = [];

    for (let i = 0; i < timeseries.length; i++) {
        const ts = timeseries[i].resampleToMonthly();
        const periodReturn: number[] = [];

        for (let j = 0; j < ranges.length; j++) {
            const m = /([0-9]+)([MY])/.exec(ranges[j] as string);

            if (m) {
                let months = parseInt(m[1], 10);
                if (m[2] === "Y") months *= 12;
                const tsReturn = ts.endValue / ts.__values[ts.length - 1 - months];
                if (months < 14) {
                    periodReturn.push(tsReturn - 1);
                } else {
                    periodReturn.push(Math.pow(tsReturn, 12 / (months - 1)) - 1);
                }
                //If the range is a year, e.g 2019, 2020, return is calculated using specificYearReturn()
            } else if (typeof ranges[j] === "number" && isFinite(Number(ranges[j]))) {
                periodReturn.push(timeseries[i].specificYearReturn(parseFloat(ranges[j] as string)));
            } else {
                periodReturn.push(Number.NaN);
            }
        }
        allReturns.push(periodReturn);
    }

    const diffs: number[][] = [];
    if (allReturns.length > 1) {
        // if at least one benchmark
        const masterSeriesReturns = allReturns[0];
        for (let j = 1; j < allReturns.length; j++) {
            const benchMarkReturn = allReturns[j];
            const diff = masterSeriesReturns.map((x, i) => parseFloat(format(x)) - parseFloat(format(benchMarkReturn[i])));
            diffs.push(diff);
        }
    }

    // Build rows: first row is the master series, subsequent rows are benchmarks with diffs
    const rows: { name: string; values: string[] }[] = [];

    // Add master series returns
    rows.push({
        name: timeseries[0].name,
        values: allReturns[0].map((e) => format(e))
    });

    // Add benchmark returns with diffs
    for (let i = 1; i < allReturns.length; i++) {
        const returns = cloneDeep(allReturns[i]);
        const diff = diffs[i - 1];
        const formattedReturns: string[] = [];
        for (let j = 0; j < returns.length; j++) {
            formattedReturns.push(format(returns[j]) + "[" + format(diff[j] / 100) + "]");
        }
        rows.push({ name: timeseries[i].name, values: formattedReturns });
    }

    return (
        <div className="my-2">
            <table className="w-full text-sm border-collapse">
                <thead>
                    <tr className="bg-white">
                        <th className="py-1.5 px-2.5 text-left font-bold"></th>
                        {ranges.map((range, i) => (
                            <th key={i} className="py-1.5 px-2.5 text-center font-bold">
                                {range}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? "bg-gray-100" : "bg-white"}>
                            <td className="py-1.5 px-2.5 font-bold text-left truncate max-w-[200px]" title={row.name}>
                                {row.name}
                            </td>
                            {row.values.map((val, colIndex) => (
                                <td key={colIndex} className="py-1.5 px-2.5 text-center">
                                    {val}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
